import { useSelector } from '@xstate/react';
import cn from 'classnames';
import type { MouseEventHandler } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Cross, Eye } from '@webapp/common/assets/icons';
import { IconFullscreen } from '@webapp/common/assets/icons/fullscreen';
import { Checkbox } from '@webapp/common/components/checkbox';
import { CDN_URL } from '@webapp/common/conf';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import { PLACEHOLDER } from '@webapp/common/resources/fillers';

import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import type { SurveyQuestion } from '../../lib';

import css from './first-click.css';

const convertWidthToOriginalPxs = (value: number, ref: HTMLImageElement) => {
    const clientWidthToOriginalWidthPx = ref.clientWidth / ref.naturalWidth;
    return Math.round(value / clientWidthToOriginalWidthPx);
};

const convertHeightToOriginalPxs = (value: number, ref: HTMLImageElement) => {
    const clientWidthToOriginalHeightPx = ref.clientHeight / ref.naturalHeight;
    return Math.round(value / clientWidthToOriginalHeightPx);
};

const convertWidthToCurrentPx = (value: number, ref: HTMLImageElement) => {
    const clientWidthToOriginalWidthPx = ref.clientWidth / ref.naturalWidth;
    return value * clientWidthToOriginalWidthPx;
};

const convertHeightToCurrentPx = (value: number, ref: HTMLImageElement) => {
    const clientHeightToOriginalHeightPx = ref.clientHeight / ref.naturalHeight;
    return value * clientHeightToOriginalHeightPx;
};

const ImageFullScreen: FC<SurveyQuestion & { isOpen: boolean; onClose: () => void; isCantRateChecked?: boolean }> = ({
    question,
    actor,
    onClose,
    isOpen,
}) => {
    const [_, update] = useState(0);
    const [wrapRef, setWrapRef] = useState<HTMLDivElement>(null);
    const [imageRef, setImageRef] = useState<HTMLImageElement>(null);
    const [computedHeight, setComputedHeight] = useState(0);
    const [computedWidth, setComputedWidth] = useState(0);
    const isByWidth = question.params.firstClickView === 'by-width';

    const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
        if (!wrapRef || !imageRef || !question.showImageTimestamp) return;
        const rect = (isByWidth ? imageRef : wrapRef)?.getBoundingClientRect();

        const x = e.clientX - rect?.left;
        const y = e.clientY - rect?.top;

        actor.send({
            type: 'CLICK_IMAGE',
            value: {
                x: convertWidthToOriginalPxs(x, imageRef),
                y: convertHeightToOriginalPxs(y, imageRef),
            },
        });
        if (question.clicks.length + 1 === question.params.clickCount) {
            onClose();
        }
    };

    useEffect(() => {
        if (!imageRef) return;

        const recalc = () => {
            const height = imageRef.clientHeight;
            const width = imageRef.clientWidth;
            const maxWidth = document.body.clientWidth;
            const maxHeight = window.innerHeight;

            if (height > maxHeight) {
                setComputedHeight(maxHeight);
            }
            if (width > maxWidth) {
                setComputedWidth(maxWidth);
            }
            update((prev) => prev + (1 % 10));
        };

        const ro = new ResizeObserver(recalc);
        ro.observe(imageRef);

        const handleLoad = () => recalc();
        imageRef.addEventListener('load', handleLoad);

        recalc();

        return () => {
            ro.disconnect();
            imageRef.removeEventListener('load', handleLoad);
        };
    }, [imageRef, question.clicks, isByWidth]);

    return (
        <div className={cn(css.imageWrapFirstClickShownZoomed, isOpen && css.imageWrapFirstClickShownZoomedOpened)}>
            <div
                ref={setWrapRef}
                className={cn(
                    css.imageWrap,
                    question.showImageTimestamp && css.imageWrapFirstClickShown,
                    isByWidth && css.byWidth,
                )}
                onClick={handleClick}
            >
                {imageRef &&
                    question.clicks?.map((click) => (
                        <div
                            key={`${click.x}-${click.y}`}
                            className={css.click}
                            style={{
                                left: convertWidthToCurrentPx(click.x, imageRef),
                                top: convertHeightToCurrentPx(click.y, imageRef),
                            }}
                        />
                    ))}
                <img
                    ref={setImageRef}
                    style={{
                        maxWidth: computedWidth || undefined,
                        maxHeight: isByWidth ? '100%' : computedHeight || undefined,
                    }}
                    src={`${CDN_URL}${question.filePath}`}
                />
                {question.showImageTimestamp && (
                    <Cross
                        className={css.crossIcon}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export const FirstClick: FC<SurveyQuestion> = ({ question, actor, readOnly }) => {
    const [_, update] = useState(0);
    const [wrapRef, setWrapRef] = useState<HTMLDivElement>(null);
    const [imageRef, setImageRef] = useState<HTMLImageElement>(null);
    const [isZoomed, setZoomed] = useState(false);
    const info = useStateSelector(selectInfo);

    const response = useSelector(question.responseActor, (r) => r.context);
    const {
        id: questionId,
        params: { cantRate, cantRateText },
    } = question;
    const isCantRateChecked = response?.value === CANTRATE_ID;

    const handleCantRate = (checked: boolean) => {
        const value = checked ? CANTRATE_ID : '';
        question.responseActor.send({ type: 'CHANGE', value, questionId });

        actor.send({
            type: 'CLICK_IMAGE',
            cantRate: checked,
        });
    };

    const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
        if (!wrapRef || !imageRef || !question.showImageTimestamp) return;
        // Вопрос уже отвечен (все клики сделаны): не трогаем response.
        // Иначе CHANGE с undefined затирал ответ, а CLICK_IMAGE в машине
        // игнорировался (clicks.length >= clickCount) — вопрос оставался
        // без ответа и без возможности кликнуть, страница «зависала».
        if (question.clicks.length >= question.params.clickCount) return;
        const rect = wrapRef?.getBoundingClientRect();
        const x = e.clientX - rect?.left;
        const y = e.clientY - rect?.top;

        question.responseActor.send({ type: 'CHANGE', value: undefined, questionId });
        actor.send({
            type: 'CLICK_IMAGE',
            value: {
                x: convertWidthToOriginalPxs(x, imageRef),
                y: convertHeightToOriginalPxs(y, imageRef),
            },
        });
    };

    useEffect(() => {
        if (!imageRef) return;
        const ro = new ResizeObserver(() => {
            update((prev) => prev + (1 % 10));
        });
        ro.observe(imageRef);
        return () => ro.disconnect();
    }, [imageRef, question.clicks]);

    return (
        <div
            className={cn(css.firstClick, {
                [css.firstClickShown]: question.showImageTimestamp,
                [css.firstClickShownZoomed]: isZoomed,
                [css.firstClickReadyOnly]: readOnly,
            })}
        >
            <div className={css.contentWrap}>
                {question.filePath && (
                    <div
                        className={cn(css.blur, question.showImageTimestamp && css.blur_hidden)}
                        onClick={() => {
                            actor.send({ type: 'SHOW_IMAGE' });
                        }}
                    >
                        <div className={css.imageTooltipWrap}>
                            <Eye className={css.eyeIcon} />
                            <div className={css.blurText}>{info.params.alert.firstClickImage}</div>
                        </div>
                    </div>
                )}
                {createPortal(
                    <ImageFullScreen
                        isOpen={question.showImageTimestamp && isZoomed}
                        question={question}
                        actor={actor}
                        onClose={() => setZoomed(false)}
                    />,
                    document.body,
                )}
                <div
                    ref={setWrapRef}
                    className={cn(css.imageWrap, {
                        [css.imageWrapFirstClickShown]: question.showImageTimestamp,
                        [css.imageWrapReadOnly]: readOnly,
                    })}
                    onClick={handleClick}
                >
                    {imageRef &&
                        question.clicks?.map((click) => (
                            <div
                                key={`${click.x}-${click.y}`}
                                className={css.click}
                                style={{
                                    left: convertWidthToCurrentPx(click.x, imageRef),
                                    top: convertHeightToCurrentPx(click.y, imageRef),
                                }}
                            />
                        ))}
                    <img ref={setImageRef} src={`${CDN_URL}${question.filePath}`} alt='image' />
                    {question.showImageTimestamp &&
                        !isZoomed &&
                        question.params.clickCount > question.clicks.length && (
                            <IconFullscreen
                                className={css.zoomIcon}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setZoomed(true);
                                }}
                            />
                        )}
                </div>
            </div>
            {cantRate && (
                <Checkbox
                    className={css.cantRateCheckbox}
                    checked={isCantRateChecked}
                    label={cantRateText || PLACEHOLDER.CANT_RATE}
                    onChange={handleCantRate}
                />
            )}
        </div>
    );
};
